// Reads an uploaded priors document in the browser, page by page, and hands each
// page's positioned words to priorsParser. Typed PDFs use their text layer; scans
// are read with OCR. The document never leaves the lawyer's machine for this.
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist/build/pdf.mjs";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { createWorker } from "tesseract.js";
import { parsePriorsPage } from "./priorsParser.js";

GlobalWorkerOptions.workerSrc = workerSrc;

const PDFJS_OPTIONS = {
  disableWorker: true,
  useSystemFonts: true,
  isEvalSupported: false,
  cMapUrl: "/pdfjs/cmaps/",
  cMapPacked: true,
  standardFontDataUrl: "/pdfjs/standard_fonts/",
};

// Fewer words than this on a page means there's no real text layer, so it's a scan.
const MIN_TEXT_ITEMS = 40;

async function ocrLines(worker, image) {
  const { data } = await worker.recognize(image, {}, { blocks: true });
  const lines = [];
  for (const block of data.blocks || []) {
    for (const para of block.paragraphs || []) {
      for (const line of para.lines || []) {
        lines.push({ words: line.words.map((w) => ({ t: w.text, x0: w.bbox.x0 })) });
      }
    }
  }
  return lines;
}

// Text-layer items grouped into lines by their baseline.
function textLayerLines(items) {
  const rows = [];
  for (const item of items) {
    if (!item.str || !item.str.trim()) continue;
    const y = item.transform[5];
    let row = rows.find((r) => Math.abs(r.y - y) < 3);
    if (!row) rows.push((row = { y, words: [] }));
    const x = item.transform[4];
    // Split each item into words, spacing them by character width so columns still line up.
    const charWidth = item.str.length ? item.width / item.str.length : 0;
    let offset = 0;
    for (const part of item.str.split(/(\s+)/)) {
      if (part.trim()) row.words.push({ t: part, x0: x + offset * charWidth });
      offset += part.length;
    }
  }
  return rows.sort((a, b) => b.y - a.y).map((r) => ({ words: r.words.sort((a, b) => a.x0 - b.x0) }));
}

/**
 * @param {File} file a PDF or an image
 * @param {{ onPage?: (page: number, total: number, hearings: any[]) => void, shouldStop?: () => boolean }} [options]
 * @returns {Promise<{ hearings: any[], pagesRead: number, totalPages: number, stopped: boolean }>}
 */
export async function readPriorsFile(file, { onPage, shouldStop } = {}) {
  const state = { hearings: [], current: null };
  /** @type {Awaited<ReturnType<typeof createWorker>> | null} */
  let worker = null;
  const getWorker = async () => {
    if (!worker) {
      worker = await createWorker("eng");
      await worker.setParameters({ preserve_interword_spaces: "1" });
    }
    return worker;
  };

  try {
    if (file.type.startsWith("image/")) {
      onPage?.(1, 1, state.hearings);
      parsePriorsPage(await ocrLines(await getWorker(), file), state);
      onPage?.(1, 1, state.hearings);
      return { hearings: state.hearings, pagesRead: 1, totalPages: 1, stopped: false };
    }

    const pdf = await getDocument({ data: new Uint8Array(await file.arrayBuffer()), ...PDFJS_OPTIONS }).promise;
    const total = pdf.numPages;
    let pagesRead = 0;

    for (let n = 1; n <= total; n += 1) {
      if (shouldStop?.()) return { hearings: state.hearings, pagesRead, totalPages: total, stopped: true };
      onPage?.(n, total, state.hearings);

      const page = await pdf.getPage(n);
      const content = await page.getTextContent();
      let lines;
      if ((content.items || []).filter((i) => i.str && i.str.trim()).length >= MIN_TEXT_ITEMS) {
        lines = textLayerLines(content.items);
      } else {
        const viewport = page.getViewport({ scale: 2 });
        const canvas = document.createElement("canvas");
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        const context = canvas.getContext("2d", { willReadFrequently: true });
        // "print" renders without waiting for screen redraws, which stop when the
        // lawyer switches to another tab during a long record.
        await page.render({ canvasContext: context, viewport, intent: "print" }).promise;
        lines = await ocrLines(await getWorker(), canvas);
        canvas.width = 0;
        canvas.height = 0;
      }
      parsePriorsPage(lines, state);
      pagesRead = n;
      page.cleanup();
    }
    onPage?.(total, total, state.hearings);
    return { hearings: state.hearings, pagesRead, totalPages: total, stopped: false };
  } finally {
    if (worker) await worker.terminate();
  }
}
